import { Build, Component, h, Prop, State, Element } from '@stencil/core';


/**
 * Responsive image component with lazy loading and aspect ratio support
 *
 * Requires srcSet for responsive behavior. Use different components for single-source images.
 *
 * @example
 *   <me-picture
 *     srcSet="image-400.jpg 400w, image-800.jpg 800w, image-1200.jpg 1200w"
 *     src="image-800.jpg"
 *     alt="Responsive example image"
 *     aspect-ratio="16/9"
 *     lazy="true"
 *   ></me-picture>
 */
@Component({
  tag: 'me-picture',
  styleUrl: 'index.scss',
  shadow: false,
})
export class MePicture {
  /**
   * Image URL (optional, will be extracted from srcSet if not provided)
   * @mediaType image
   */
  @Prop() src?: string;
  /**
   * Alt text for the image (optional)
   * @content
   */
  @Prop() alt: string = '';
  /** Aspect ratio (required, e.g. '16/9') */
  @Prop() aspectRatio!: string;
  /** Enable lazy loading (default: true). Accepts boolean or string ('true'/'false'). */
  @Prop() lazy: boolean | string = true;
  /** Width of the image (optional, e.g. 56, '56px', '4rem') */
  @Prop() width?: number | string;
  /** Height of the image (optional, e.g. 56, '56px', '4rem') */
  @Prop() height?: number | string;
  /**
   * Source set for different resolutions (required, e.g. 'image.png 1x, image@2x.png 2x')
   * @mediaType image
   */
  @Prop() srcSet: string;
  /**
   * WebP source set for different resolutions (optional, e.g. 'image.webp 1x, image@2x.webp 2x')
   * @mediaType image
   */
  @Prop() webpSrcSet?: string;
  /** Fetch priority hint for the image (optional, e.g. 'high', 'low', 'auto') */
  @Prop() fetchPriority?: 'high' | 'low' | 'auto';
  /**
   * Media query for conditional image loading. When set, the image is only downloaded
   * on viewports matching this query. On non-matching viewports a transparent pixel is
   * rendered instead and no network request is made.
   * Example: "(min-width: 1024px)" to load only on desktop/tablet.
   */
  @Prop() media?: string;

  @State() inView: boolean = false;

  @Element() hostEl!: HTMLElement;
  private observer?: IntersectionObserver;

  /**
   * Normalize the lazy prop to a boolean value.
   */
  private get isLazy(): boolean {
    if (typeof this.lazy === 'string') {
      return this.lazy === 'true';
    }
    return !!this.lazy;
  }

  componentWillLoad() {
    // During SSR always render the full picture so the server-rendered HTML is not
    // an empty placeholder. An empty placeholder causes Stencil to leave the old
    // SSR element in the DOM when the client re-renders with inView=true, producing
    // a duplicate div.picture node.
    if (!Build.isBrowser) {
      this.inView = true;
      return;
    }
    // For client-side: render images immediately if lazy is false
    if (!this.isLazy) {
      this.inView = true;
      return;
    }
    // Lazy + client (hydration): if the picture was already server-rendered, keep it
    // rendered instead of tearing it down and rebuilding it. Resetting inView to false
    // here makes Stencil remove the SSR <picture> and recreate it when the observer
    // fires — and rebuilding re-fetches the image (and can fetch the <img> png fallback
    // before the <source webp> attaches, producing a duplicate download). Native
    // loading="lazy" on the <img> already defers the off-screen fetch, so keeping the
    // server markup is safe and avoids the double load.
    if (this.hostEl?.querySelector('picture, img')) {
      this.inView = true;
    }
  }

  /**
   * Use IntersectionObserver to load the image only when it is near the viewport.
   * rootMargin is set to '200px' for preloading before the image is visible.
   * Note: inView state is set in componentWillLoad to avoid Stencil warnings.
   */
  componentDidLoad() {
    // Set up IntersectionObserver only when we still need to reveal an image that
    // isn't rendered yet (client-created, off-screen). SSR-rendered/kept images and
    // non-lazy images already have inView=true, so no teardown/rebuild is needed.
    if (this.isLazy && !this.inView) {
      this.observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.inView = true;
              this.observer?.disconnect();
            }
          });
        },
        { rootMargin: '200px' }, // Preload before the image is visible
      );
      this.observer.observe(this.hostEl);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  render() {
    // Prepare style object (aspect-ratio only; width/height go to <img> as attributes)
    const style: Record<string, string> = {
      aspectRatio: this.aspectRatio,
    };

    // Transparent 1x1 GIF used as fallback when media query doesn't match
    const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

    // Prepare img attributes object (alt is set explicitly for linting)
    const imgAttributes: any = {};

    if (this.fetchPriority) {
      imgAttributes.fetchpriority = this.fetchPriority;
    }

    // Native lazy loading, complementing the IntersectionObserver below.
    // The observer only defers client-rendered images, but the server-rendered markup
    // ships real URLs that the browser fetches eagerly before hydration — so an
    // off-screen image downloads regardless of the observer. `loading="lazy"` lets the
    // browser defer that initial fetch on SSR pages too. Skipped for non-lazy images
    // (e.g. LCP heroes with lazy="false"/fetch-priority="high") to keep them eager.
    if (this.isLazy) {
      imgAttributes.loading = 'lazy';
    }

    // HTML img width/height attributes require integers (pixels).
    // Props may arrive as numbers (80) or CSS strings ("80px", "4rem").
    // Only pass pure-integer values as HTML attributes; CSS units go via inline style.
    if (this.width !== undefined) {
      const w = typeof this.width === 'number' ? this.width : parseInt(String(this.width), 10);
      if (!isNaN(w)) imgAttributes.width = w;
    }
    if (this.height !== undefined) {
      const h = typeof this.height === 'number' ? this.height : parseInt(String(this.height), 10);
      if (!isNaN(h)) imgAttributes.height = h;
    }

    // When media prop is set, use native <source media="..."> so the browser
    // skips the download entirely on non-matching viewports.
    if (this.media) {
      return (
        <div class="picture" style={style}>
          {this.inView && (
            <picture>
              {this.webpSrcSet && <source media={this.media} type="image/webp" srcSet={this.webpSrcSet} />}
              <source media={this.media} srcSet={this.srcSet || this.src} />
              <img src={TRANSPARENT_PIXEL} alt={this.alt} {...imgAttributes} />
            </picture>
          )}
        </div>
      );
    }

    // Determine which sources to use based on available props
    const hasWebpSrcSet = !!this.webpSrcSet;
    const hasSrcSet = !!this.srcSet;
    const needsPictureElement = hasWebpSrcSet || hasSrcSet;

    // Extract fallback src from srcSet if no src provided but srcSet exists
    let fallbackSrc = this.src;
    let imgSrcSet = this.srcSet;

    if (hasSrcSet) {
      // Parse srcSet to extract 1x image as fallback src and remove 1x from srcSet
      const srcSetParts = this.srcSet.split(',').map(s => s.trim());
      const srcSetFiltered: string[] = [];

      for (const part of srcSetParts) {
        const [url, descriptor = '1x'] = part.split(/\s+/);

        // Use 1x as fallback src
        if (descriptor === '1x' && !fallbackSrc) {
          fallbackSrc = url;
        }

        // Only add non-1x versions to imgSrcSet (2x, 3x, etc.)
        if (descriptor !== '1x') {
          srcSetFiltered.push(part);
        }
      }

      // Update imgSrcSet to only contain 2x, 3x, etc.
      imgSrcSet = srcSetFiltered.length > 0 ? srcSetFiltered.join(', ') : undefined;
    }

    return (
      <div class="picture" style={style}>
        {this.inView && needsPictureElement ? (
          <picture>
            {/* WebP source when available */}
            {hasWebpSrcSet && <source type="image/webp" srcSet={this.webpSrcSet} />}
            {/*
              Put regular srcSet on img element (not as separate source)
              This allows browser to use webp if supported, or fall back to srcSet on img
              Note: imgSrcSet contains only 2x, 3x, etc. (1x is already in src)
            */}
            <img src={fallbackSrc} srcSet={imgSrcSet} alt={this.alt} {...imgAttributes} />
          </picture>
        ) : (
          this.inView && (
            /* Simple img when no srcSet provided */
            <img src={this.src} alt={this.alt} {...imgAttributes} />
          )
        )}
      </div>
    );
  }
}
